import React, { useEffect } from "react";
import { useParams } from "react-router-dom";
import { Auth, useAuth } from "../auth/AuthContext";
import { issueTextOrDefault } from "../DAO";
import { DocumentTitle } from "../DocumentTitle";
import { FieldLayout, ReportStore, useReportStore } from "./ReportContext";
import { UsageReport } from "./UsageReport";
import { BillingReport } from "./BillingReport";

export const ReportTable = ({ showEmpty }: { showEmpty: boolean }): JSX.Element => {
   const auth: Auth = useAuth();
   if (!auth?.user?.permissions?.instance?.canView) {
      return <div>You do not have permission to be here.</div>;
   }

   const reportStore: ReportStore = useReportStore();
   const params = useParams();
   useEffect(() => {
      const path = params["*"];
      if (path === "billing") {
         reportStore?.setReportType("BILLING");
      } else {
         reportStore?.setReportType("USAGE");
      }
   }, [params]);

   const layout = reportStore?.type === "BILLING" ? BillingReport.getLayout() : UsageReport.getLayout();
   const headers = layout.map((f, ii) =>
      f.styleHint != null ? (
         <th key={`h-${ii}`} style={f.styleHint}>
            {f.label}
         </th>
      ) : (
         <th key={`h-${ii}`}>{f.label}</th>
      )
   );
   const reportData = reportStore?.type === "BILLING" ? reportStore?.billingData : reportStore?.usageData;

   const rowRenderer = (row: UsageReport | BillingReport): JSX.Element => {
      return showEmpty || row.hasData() ? (
         <tr key={row.id}>
            {layout.map((f, ii) => (
               <ReportCell key={`rc-${row.id}-${ii}`} rowData={row} fieldLayout={f} />
            ))}
         </tr>
      ) : (
         <React.Fragment key={row.id} />
      );
   };

   return (
      <DocumentTitle title={reportStore?.type === "USAGE" ? "Usage" : "Billing"}>
         <table border={1} cellPadding="3px">
            <thead>
               <tr>{headers}</tr>
            </thead>
            <tbody>
               {(reportStore?.isLoading && (
                  <tr>
                     <td colSpan={headers.length}>Loading...</td>
                  </tr>
               )) ||
                  (reportStore?.error?.issues?.length && (
                     <>
                        {reportStore.error.issues
                           .map(issueTextOrDefault)
                           .map((msg) => (
                              <tr>
                                 <td colSpan={headers.length}>{msg}</td>
                              </tr>
                           ))
                           .join("")}
                        <button type="button" className="link" onClick={reportStore.loadReportForType}>
                           try again
                        </button>
                     </>
                  )) ||
                  (reportData?.length && reportData.map(rowRenderer)) ||
                  (!reportData?.length && (
                     <tr>
                        <td colSpan={headers.length}>No records found.</td>
                     </tr>
                  ))}
            </tbody>
         </table>
      </DocumentTitle>
   );
};
type CellProps = {
   fieldLayout: FieldLayout;
   rowData: UsageReport | BillingReport;
};
const ReportCell = (props: CellProps): JSX.Element =>
   props.fieldLayout.styleHint != null ? (
      <td style={props.fieldLayout.styleHint}>{props.fieldLayout.toString(props.rowData)}</td>
   ) : (
      <td>{props.fieldLayout.toString(props.rowData)}</td>
   );
